import { createFileRoute, Link } from "@tanstack/react-router";
import type { User } from "@supabase/supabase-js";
import { useEffect, useState } from "react";
import { ArrowRight } from "lucide-react";

import { supabase } from "@/integrations/supabase/client";
import { Breadcrumbs } from "@/components/breadcrumbs";
import { PricingSection } from "@/components/landing/pricing-section";
import { FaqSection } from "@/components/landing/faq-section";
import { SiteHeader, SiteFooter } from "@/components/site-chrome";
import { MobileCtaBar } from "@/components/landing/mobile-cta-bar";
import { breadcrumbJsonLd, SITE_URL, BRAND_OG_IMAGE, BRAND_OG_IMAGE_ALT, SITE_NAME } from "@/lib/seo";
import { useTranslation } from "react-i18next";

const title = "Pricing: Kids English & IELTS Classes | Smart Word Labs";
const description =
  "Transparent monthly plans from $119 for live online English classes for kids 4-12 and private IELTS coaching for all ages. Free assessment included.";

export const Route = createFileRoute("/pricing")({
  head: () => ({
    meta: [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:type", content: "website" },
      { property: "og:url", content: `${SITE_URL}/pricing` },
      { property: "og:site_name", content: SITE_NAME },
      { property: "og:image", content: BRAND_OG_IMAGE },
      { property: "og:image:alt", content: BRAND_OG_IMAGE_ALT },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: BRAND_OG_IMAGE },
      { name: "twitter:image:alt", content: BRAND_OG_IMAGE_ALT },
      { name: "twitter:title", content: title },
      { name: "twitter:description", content: description },
    ],
    links: [{ rel: "canonical", href: `${SITE_URL}/pricing` }],
    scripts: [breadcrumbJsonLd([{ name: "Pricing", path: "/pricing" }])],
  }),
  component: PricingPage,
});

function PricingPage() {
  const { t } = useTranslation("pages");
  const [user, setUser] = useState<User | null>(null);

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => setUser(data.user ?? null));
    const { data } = supabase.auth.onAuthStateChange((_e, session) =>
      setUser(session?.user ?? null),
    );
    return () => data.subscription.unsubscribe();
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <Breadcrumbs trail={[{ name: t("pricing.breadcrumb"), path: "/pricing" }]} />

      <main className="pb-20 lg:pb-0">
        <PricingSection user={user} headingLevel="h1" />

        <div className="mx-auto max-w-6xl px-5 pb-4">
          <Link
            to="/programmes"
            className="group inline-flex items-center gap-2 text-base font-semibold text-primary transition-colors hover:text-accent"
          >
            {t("pricing.compareLink")}
            <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
          </Link>
        </div>

        <FaqSection />
      </main>

      <SiteFooter />
      <MobileCtaBar />
    </div>
  );
}
